<template>
  <div class="home-blockquote">
    <div class="home-blockquote__title">
      <sc-text tag="h2" v-if="fields && fields.heading" :field="fields.heading" />
    </div>
    <div class="home-blockquote__quotes">
      <template v-if="isEditing">
        <!--
          When experience editor is active we want to render all carousel slides, to simplify editing.
        -->
        <sc-placeholder name="jss-home-blockquotes" :rendering="rendering" />
      </template>
      <template v-else>
        <!--
          We only want to render the carousel when we're not editing.
        -->
        <Splide
          ref="splide"
          @splide:resize="onResize"
          @splide:active="onActive"
          :options="options"
          aria-label="Quotes"
        >
          <SplideSlide
            ref="SplideSlide"
            v-for="(quote, index) in $options.computed.quotePlaceholder"
            :is="quote"
            :key="`quote${index}`"
          >
            <component :is="quote" :key="`quote${index}`" />
          </SplideSlide>
        </Splide>
      </template>
    </div>
  </div>
</template>

<script>
import { Text, Placeholder } from '@sitecore-jss/sitecore-jss-vue';
import { ref, reactive, onMounted, nextTick } from 'vue';
import { Splide, SplideSlide } from '@splidejs/vue-splide';
import '@splidejs/vue-splide/css/core';

export default {
  name: 'CIS-Home-Blockquote',
  components: {
    ScText: Text,
    Splide,
    SplideSlide,
    ScPlaceholder: Placeholder
  },
  props: {
    rendering: {
      type: Object
    },
    fields: {
      type: Object
    }
  },
  withPlaceholder() {
    return {
      placeholders: [
        {
          placeholder: 'jss-home-blockquotes',
          computedPropName: 'quotePlaceholder'
        }
      ]
    };
  },
  setup() {
    const splide = ref();
    const SplideSlide = ref();

    const options = reactive({
      type: 'fade',
      arrows: false,
      rewind: true,
      autoplay: true,
      speed: 350,
      interval: 8000
    });

    const onActive = (item) => {
      const slideHeight = SplideSlide?.value[item.index]?.$el?.children[0]?.offsetHeight;
      options.height = slideHeight + 'px';
    };

    const onResize = (item) => {
      const slideHeight = SplideSlide?.value[item.index]?.$el?.children[0]?.offsetHeight;
      options.height = slideHeight + 'px';
    };

    onMounted(() => {
      nextTick(() => {
        const attributesToRemove = ['role', 'aria-label', 'aria-roledescription', 'aria-hidden'];

        document.querySelectorAll('.splide__slide').forEach((el) => {
          attributesToRemove.forEach((att) => {
            el.removeAttribute(att);
          });
        });
      });
    });

    return { splide, SplideSlide, options, onActive, onResize };
  },
  computed: {
    isEditing() {
      // this.$jss is defined on the Vue instance by the SitecoreJssPlugin and provides
      // reactive access to the `sitecoreContext` provided in layout service data.
      return this.$jss.sitecoreContext().pageEditing;
    }
  }
};
</script>

<style lang="scss">
.home-blockquote {
  @include padding-y(5rem);
  @include padding-x-thick;
  padding-bottom: 0.7rem;

  @include tablet-up {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    @include padding-y(6rem);
    padding-bottom: 1.6rem;
  }

  @include desktop-up {
    @include padding-y(8rem);
    padding-bottom: 3.6rem;
  }

  &__title {
    width: 100%;

    @include tablet-up {
      width: calc(35% - 6rem);
      margin-right: 6rem;
    }

    h2 {
      font-size: 2.4rem;
      font-weight: 700;
      line-height: 3rem;
      padding-bottom: 1rem;

      @include tablet-up {
        font-size: 3.6rem;
        line-height: 4.5rem;
      }
    }
  }

  &__quotes {
    position: relative;
    width: 100%;
    padding-left: 4.5rem;
    padding-right: 2rem;

    @include tablet-up {
      width: 65%;
      padding-left: 8rem;
      padding-right: 0;
    }

    &:before {
      content: '';
      display: block;
      position: absolute;
      top: 0.8rem;
      left: 0;
      width: 2.95rem;
      height: 2.27rem;
      // background-size: cover;
      // background-image: url(~@/assets/images/hp_blockquote.png);
      -webkit-mask: url(~@/assets/images/hp_blockquote.png);
      mask: url(~@/assets/images/hp_blockquote.png);
      mask-size: cover;
      background-color: $orange;

      @include tablet-up {
        top: 1rem;
        width: 5.2rem;
        height: 4rem;
      }
    }
  }
}
</style>
